import { createFileRoute } from "@tanstack/react-router";
import type { Briefing, BriefingDocument } from "@veritab/api-contracts";
import { FileText, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  BriefingDocumentListItem,
  BriefingListItem,
  UploadBriefingDocumentDialog,
  useBriefingDocumentsQuery,
  useBriefingsQuery,
} from "@/features/briefings";
import { usePageHeader } from "@/shared/store/page-header";
import { Button } from "@/shared/ui";

export const Route = createFileRoute("/_authenticated/briefings/")({
  component: AllBriefingsPage,
});

type BriefingFeedItem =
  | { kind: "generated"; id: string; createdAt: number; briefing: Briefing }
  | { kind: "uploaded"; id: string; createdAt: number; document: BriefingDocument };

function AllBriefingsPage() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const { data, isLoading: briefingsLoading } = useBriefingsQuery();
  const { data: documentsData, isLoading: documentsLoading } = useBriefingDocumentsQuery();
  const { setTitle, reset } = usePageHeader();
  const isLoading = briefingsLoading || documentsLoading;

  const items = useMemo<BriefingFeedItem[]>(() => {
    const generated: BriefingFeedItem[] = (data?.briefings ?? []).map((briefing) => ({
      kind: "generated",
      id: briefing.id,
      createdAt: new Date(briefing.createdAt).getTime(),
      briefing,
    }));
    const uploaded: BriefingFeedItem[] = (documentsData?.documents ?? []).map((document) => ({
      kind: "uploaded",
      id: document.id,
      createdAt: new Date(document.createdAt).getTime(),
      document,
    }));
    return [...generated, ...uploaded].sort((a, b) => b.createdAt - a.createdAt);
  }, [data?.briefings, documentsData?.documents]);

  useEffect(() => {
    setTitle("All Reports");
    return () => reset();
  }, [setTitle, reset]);

  return (
    <div className="p-4 sm:p-8 space-y-8 max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">All Reports</h1>
          <p className="mt-1.5 text-sm text-muted-foreground/70">
            Generated briefing reports and uploaded PDFs
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="rounded-lg shadow-sm">
          <Plus className="mr-1.5 h-4 w-4" />
          Upload PDF
        </Button>
      </div>

      <div className="h-px bg-gradient-to-r from-border/60 via-border/30 to-transparent" />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[100px] rounded-xl border border-border/40 bg-muted/10 animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="rounded-2xl bg-muted/40 p-5 mb-6">
            <FileText className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1.5">No briefings yet</h3>
          <p className="text-sm text-muted-foreground/60 max-w-xs leading-relaxed">
            Generate a briefing from the Instructions page or upload a PDF to see it here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div
              key={`${item.kind}-${item.id}`}
              className="animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: `${i * 60}ms`, animationFillMode: "backwards" }}
            >
              {item.kind === "generated" ? (
                <BriefingListItem briefing={item.briefing} />
              ) : (
                <BriefingDocumentListItem document={item.document} />
              )}
            </div>
          ))}
        </div>
      )}

      <UploadBriefingDocumentDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
